import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { useHistory, useParams } from "react-router";
import { getApiUrl } from "../../api-clients";
import {
  ClassroomApi,
  ClassroomDto,
  StudentApi,
  StudentView,
  TeacherClassroomDto,
} from "../../api-clients/api";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import LabelFieldValue from "../Shared/LabelFieldValue";
import FormButtons from "../Shared/FormButtons";
import CRUDClassroom from "../CRUD/CRUDClassroom";
import ClassroomSchedule from "./ClassroomSchedule";
import ClassroomExams from "../Exams/ClassroomExams";
import { Panel } from "@fluentui/react";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import { SchoolContext } from "../../contexts/SchoolContext";
import { AppContext } from "../../contexts/AppContext";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

export default function Classroom() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { classroomId }: any = useParams();
  const history = useHistory();
  const { accademicYear } = useContext(SchoolContext);
  const { openSnackbar } = useContext(AppContext);
  const [classroomApi] = useState<ClassroomApi>(new ClassroomApi(getApiUrl()));
  const [studentApi] = useState<StudentApi>(new StudentApi(getApiUrl()));
  const [classroom, setClassroom] = useState<ClassroomDto | null>(null);
  const [classroomLoaded, setClassroomLoaded] = useState<boolean>(false);
  const [classroomToEdit, setClassroomToEdit] = useState<ClassroomDto | null>(
    null
  );
  const [checkDelete, setCheckDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);

  const [teachers, setTeachers] = useState<TeacherClassroomDto[]>([]);
  const [teachersLoading, setTeachersLoading] = useState<boolean>(true);
  const [students, setStudents] = useState<StudentView[]>([]);
  const [studentsLoading, setStudentsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (classroom == null) {
      setClassroomLoaded(false);
      classroomApi
        .getById(classroomId)
        .then((res) => {
          setClassroom(res);
          setClassroomLoaded(true);
        })
        .catch(() => setClassroomLoaded(true));
    }
  }, [classroomApi, classroomId, classroom]);

  useEffect(() => {
    if (accademicYear?.accademicYearId == null) return;
    setTeachersLoading(true);
    classroomApi
      .getTeachers(classroomId, accademicYear.accademicYearId)
      .then((res) => {
        setTeachers(res);
        setTeachersLoading(false);
      })
      .catch(() => setTeachersLoading(false));

    setStudentsLoading(true);
    studentApi
      .classroom(classroomId, accademicYear.accademicYearId)
      .then((res) => {
        setStudents(res);
        setStudentsLoading(false);
      })
      .catch(() => setStudentsLoading(false));
  }, [classroomApi, studentApi, classroomId, accademicYear]);

  const deleteClassroom = () => {
    setDeleting(true);
    classroomApi
      .delete(classroomId)
      .then(() => {
        setDeleting(false);
        history.push("/classrooms");
      })
      .catch(() => {
        setDeleting(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  return (
    <Container maxWidth="lg">
      {classroomLoaded ? (
        classroom != null ? (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h5">{classroom.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item sm={4} xs={12}>
                        <LabelFieldValue
                          label={t("classrooms.name")}
                          value={classroom.name}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <LabelFieldValue
                          label={t("classrooms.acronym")}
                          value={classroom.acronym}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <LabelFieldValue
                          label={t("classrooms.education")}
                          value={classroom.educationName}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions disableSpacing>
                    {!isAdmin ? null : checkDelete ? (
                      <Grid container alignItems="center">
                        <Grid item xs={2}>
                          <Typography variant="subtitle1">
                            {t("common.confirmDelete")}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            aria-label={t("common.cancel")}
                            disabled={deleting}
                            onClick={() => setCheckDelete(false)}
                            color="inherit"
                          >
                            <ClearIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.confirm")}
                            disabled={deleting}
                            onClick={deleteClassroom}
                            color="secondary"
                          >
                            <CheckIcon />
                          </IconButton>
                          {deleting && (
                            <CircularProgress
                              size={24}
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: -12,
                                marginLeft: -12,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      <FormButtons
                        onConfirmClick={() =>
                          setClassroomToEdit({ ...classroom } as ClassroomDto)
                        }
                        onDeleteClick={() => setCheckDelete(true)}
                        disabled={false}
                        cancelButtonVisible={false}
                        deleteButtonVisible={true}
                        confirmIcon="Edit"
                        confirmText={t("common.edit")}
                      />
                    )}
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            <AppBar
              position="static"
              color="default"
              style={{ marginTop: "16px" }}
            >
              <Tabs
                value={tab}
                onChange={(event, newValue) => setTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label={t("classrooms.overview")} />
                <Tab label={t("exams.title")} />
              </Tabs>
            </AppBar>

            {tab === 0 && (
              <Grid container spacing={1} style={{ marginTop: "8px" }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title={t("classrooms.teachers")} />
                    <CardContent>
                      {!teachersLoading ? (
                        teachers.length > 0 ? (
                          <List>
                            {teachers.map((tch) => (
                              <div key={tch.teacherId}>
                                <ListItem
                                  button
                                  onClick={() =>
                                    history.push("/teacher/" + tch.teacherId)
                                  }
                                  style={{
                                    paddingTop: "0px",
                                    paddingBottom: "0px",
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar>
                                      <PersonIcon />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText primary={tch.teacherName} />
                                </ListItem>
                                <Divider />
                              </div>
                            ))}
                          </List>
                        ) : (
                          <NoResult />
                        )
                      ) : (
                        <Loading />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title={t("classrooms.students")} />
                    <CardContent>
                      {!studentsLoading ? (
                        students.length > 0 ? (
                          <List>
                            {students.map((s) => (
                              <div key={s.studentId}>
                                <ListItem
                                  button
                                  onClick={() =>
                                    history.push("/student/" + s.studentId)
                                  }
                                  style={{
                                    paddingTop: "0px",
                                    paddingBottom: "0px",
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar>
                                      {(s.firstName ?? " ")[0]}
                                      {(s.lastName ?? " ")[0]}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={`${s.lastName ?? ""} ${
                                      s.firstName ?? ""
                                    }`}
                                  />
                                </ListItem>
                                <Divider />
                              </div>
                            ))}
                          </List>
                        ) : (
                          <NoResult />
                        )
                      ) : (
                        <Loading />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <ClassroomSchedule classroomId={classroomId} />
                </Grid>
              </Grid>
            )}
            {tab === 1 && (
              <div style={{ marginTop: "8px" }}>
                <ClassroomExams classroomId={classroomId} />
              </div>
            )}
          </>
        ) : (
          <NoResult />
        )
      ) : (
        <Loading />
      )}
      <Panel
        isBlocking={true}
        headerText={t("classrooms.editTitle")}
        isOpen={isAdmin && classroomToEdit != null}
        onDismiss={() => setClassroomToEdit(null)}
        closeButtonAriaLabel={t("common.close")}
      >
        {classroomToEdit != null && (
          <CRUDClassroom
            classroom={classroomToEdit}
            classroomChanged={() => {
              setClassroom(null);
              setClassroomToEdit(null);
            }}
          />
        )}
      </Panel>
    </Container>
  );
}
